// userAppGradients — per-user color override layer for app gradients.
//
// APP_GRADIENTS (./app-gradients.ts) is the system default. This module adds
// a localStorage layer so users can pick custom colors per app from the
// Icon Library Hub (/dashboard/icon-library) without touching the source.
//
// Mirrors the persistence pattern of src/lib/sidebar/sidebarCustomization.ts:
//   - load/save via localStorage
//   - cross-component invalidation via a window event
//
// Cross-device sync is intentionally NOT supported — color is per-browser
// visual taste. If we ever need it, the same shape maps cleanly onto a
// user-scoped Supabase table later.

import { useEffect, useState } from "react";
import { APP_GRADIENTS, getAppGradient } from "./app-gradients";

const STORAGE_KEY = "cc-user-app-gradients-v1";
const EVENT_NAME = "cc-user-app-gradients-change";

export type AppGradient = [string, string];
export type UserAppGradients = Record<string, AppGradient>;

function isValidGradient(v: unknown): v is AppGradient {
  return Array.isArray(v)
    && v.length === 2
    && typeof v[0] === "string"
    && typeof v[1] === "string";
}

/** Load all per-user app gradient overrides. Returns {} on SSR or error. */
export function loadUserAppGradients(): UserAppGradients {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    const out: UserAppGradients = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (isValidGradient(v)) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

/** Replace the entire override map. Used by clearUserAppGradients + import. */
export function saveAllUserAppGradients(next: UserAppGradients): void {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event(EVENT_NAME));
  } catch {
    // ignore quota / serialization errors — fallback path still works
  }
}

/** Set the override for one app. Fires the change event. */
export function saveUserAppGradient(id: string, from: string, to: string): void {
  const cur = loadUserAppGradients();
  cur[id] = [from, to];
  saveAllUserAppGradients(cur);
}

/** Remove the override for one app (revert to APP_GRADIENTS default). */
export function clearUserAppGradient(id: string): void {
  const cur = loadUserAppGradients();
  if (!(id in cur)) return;
  delete cur[id];
  saveAllUserAppGradients(cur);
}

/** Nuke all overrides. */
export function clearAllUserAppGradients(): void {
  saveAllUserAppGradients({});
}

// ─── Cached override map ──────────────────────────────────────
// getEffectiveGradient is called once per visible tile in Sidebar (~30
// calls per render). Reading + parsing localStorage on every call burns
// time. We cache the parsed map at module level and invalidate the cache
// when the change event fires.

let cachedOverrides: UserAppGradients | null = null;
let cacheListenerWired = false;

function ensureCacheListener(): void {
  if (typeof window === "undefined" || cacheListenerWired) return;
  cacheListenerWired = true;
  window.addEventListener(EVENT_NAME, () => { cachedOverrides = null; });
  // Also invalidate on cross-tab storage events (other tab wrote to LS).
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) cachedOverrides = null;
  });
}

function getCachedOverrides(): UserAppGradients {
  if (cachedOverrides) return cachedOverrides;
  ensureCacheListener();
  cachedOverrides = loadUserAppGradients();
  return cachedOverrides;
}

/**
 * Get the effective gradient for an app id:
 *   user override > APP_GRADIENTS default > slate fallback
 *
 * Safe on SSR (returns the default). Read inline at render time;
 * consumers that need to re-render on change subscribe to
 * USER_APP_GRADIENTS_EVENT via window.addEventListener.
 *
 * Internal cache: localStorage is parsed once per change (event-invalidated)
 * — calls within the same render burst share the parsed map.
 */
export function getEffectiveGradient(id: string): AppGradient {
  if (typeof window !== "undefined") {
    const overrides = getCachedOverrides();
    const v = overrides[id];
    if (isValidGradient(v)) return v;
  }
  return APP_GRADIENTS[id] ? (APP_GRADIENTS[id] as AppGradient) : getAppGradient(id);
}

/** Event consumers listen to so they can re-read after a save. */
export const USER_APP_GRADIENTS_EVENT = EVENT_NAME;

/**
 * Hook: returns a counter that bumps every time a user gradient override is
 * saved/cleared. Components that read getEffectiveGradient() at render time
 * use this to force a re-render. Discard the return value if you just want
 * the subscription side-effect.
 */
export function useUserAppGradientsRev(): number {
  const [rev, setRev] = useState(0);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const sync = () => setRev((v) => v + 1);
    window.addEventListener(EVENT_NAME, sync);
    return () => window.removeEventListener(EVENT_NAME, sync);
  }, []);
  return rev;
}
